import { c as _c } from "react/compiler-runtime";
import { FaroTableBulkActionsButtons } from "@components/common/faro-table/faro-table-bulk-actions-buttons";
import { FaroTextButton } from "@components/common/faro-text-button";
import { CloseIcon, Divider, grey, IconButton, Text, Tooltip } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Stack } from "@mui-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { sidebarSelector } from "@store/ui/ui-selector";
import { withEllipsis } from "@styles/common-styles";
import { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_MINIFIED } from "@utils/ui-utils";

/** Returns the current sidebar pixel width based on screen size and sidebar state */
function getSidebarWidth(isScreenSmOrLarger, sidebar) {
  if (!isScreenSmOrLarger || !sidebar.isVisible) {
    return 0;
  }
  return sidebar.isFullSize ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_MINIFIED;
}
/**
 * Renders a floating action bar at the bottom of the table when rows are selected.
 *
 * Displays the number of selected rows, action buttons for bulk operations,
 * and a close button to clear the selection. The bar is absolutely positioned
 * within the table container and appears with a dark background.
 */
export function FaroTableBulkActions(t0) {
  const $ = _c(37);
  const {
    bulkActionsButtons,
    numberOfSelectedRows,
    isLoading,
    allRowIds,
    isSelectAllAvailable: t1
  } = t0;
  const isSelectAllAvailable = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  const mode = useBulkActionsMode();
  const {
    isScreenSmOrLarger
  } = useMediaQueryList();
  const sidebar = useAppSelector(sidebarSelector);
  const sidebarWidth = getSidebarWidth(isScreenSmOrLarger, sidebar);
  let t2;
  if ($[0] !== allRowIds || $[1] !== dispatch) {
    t2 = function handleSelectAll() {
      if (allRowIds && allRowIds.length > 0) {
        dispatch(setInitialFetchingItems(allRowIds));
      }
    };
    $[0] = allRowIds;
    $[1] = dispatch;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleSelectAll = t2;
  if (!numberOfSelectedRows) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        position: "absolute"
      };
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== bulkActionsButtons || $[5] !== isLoading) {
      t4 = <Stack sx={t3}><FaroTableBulkActionsButtons bulkActionsButtons={bulkActionsButtons} isLoading={isLoading} /></Stack>;
      $[4] = bulkActionsButtons;
      $[5] = isLoading;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  const isCollapsedMode = mode === "collapsed";
  const t3 = `calc(50% + ${sidebarWidth / 2}px)`;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      flexDirection: "row",
      position: "fixed",
      bottom: 20,
      left: t3,
      transform: "translateX(-50%)",
      borderRadius: "4px",
      background: grey[950],
      height: 48,
      alignItems: "center",
      px: 1.5,
      zIndex: 99,
      gap: 1.5,
      whiteSpace: "nowrap"
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `${numberOfSelectedRows} selected`;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      maxWidth: "5rem"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...withEllipsis
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== numberOfSelectedRows) {
    t8 = <Stack sx={t6}><Text component="var" variant="bodyM" color={grey[0]} sx={t7}>{numberOfSelectedRows} selected</Text></Stack>;
    $[11] = numberOfSelectedRows;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t8) {
    t9 = <Tooltip component="div" title={t5}>{t8}</Tooltip>;
    $[13] = t5;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== allRowIds || $[17] !== handleSelectAll || $[18] !== isCollapsedMode || $[19] !== isSelectAllAvailable) {
    t10 = (isCollapsedMode || isSelectAllAvailable) && allRowIds.length > 1 && <FaroTextButton onClick={handleSelectAll} tooltipText="Select all" rippleColor={grey[500]} sx={{
      color: grey[0],
      fontSize: "0.875rem"
    }}>Select all</FaroTextButton>;
    $[16] = allRowIds;
    $[17] = handleSelectAll;
    $[18] = isCollapsedMode;
    $[19] = isSelectAllAvailable;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Divider orientation="vertical" dark={true} sx={{
      height: 36
    }} />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== bulkActionsButtons || $[23] !== isLoading) {
    t12 = <FaroTableBulkActionsButtons bulkActionsButtons={bulkActionsButtons} isLoading={isLoading} />;
    $[22] = bulkActionsButtons;
    $[23] = isLoading;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Divider orientation="vertical" dark={true} sx={{
      height: 36
    }} />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== dispatch) {
    t14 = () => dispatch(setInitialFetchingItems([]));
    $[26] = dispatch;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <CloseIcon />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14) {
    t16 = <IconButton size="m" margin={0.5} color={grey[0]} hoverColor={grey[0]} onClick={t14}>{t15}</IconButton>;
    $[29] = t14;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t10 || $[32] !== t12 || $[33] !== t16 || $[34] !== t4 || $[35] !== t9) {
    t17 = <Stack sx={t4}>{t9}{t10}{t11}{t12}{t13}{t16}</Stack>;
    $[31] = t10;
    $[32] = t12;
    $[33] = t16;
    $[34] = t4;
    $[35] = t9;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}